import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import logo from '../assets/logito.png';

function classNames(...c) { return c.filter(Boolean).join(' '); }

// === ICONS ==================================================================
const Icon = {
  Home: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-5 h-5" {...props}>
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  ),
  Services: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-5 h-5" {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  About: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-5 h-5" {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M6 21v-2a6 6 0 0112 0v2" />
    </svg>
  ),
  Contact: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-5 h-5" {...props}>
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  Menu: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-7 h-7" {...props}>
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  ),
  Close: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="w-7 h-7" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  Chevron: ({ open }) => (
    <svg viewBox="0 0 24 24"
      className={classNames('w-4 h-4 transition-transform duration-300',
        open && 'rotate-180')}
      stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Whatsapp: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c...Z" />
    </svg>
  ),
  ExternalLink: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14L21 3" />
    </svg>
  )
};

// ============================================================================

export default function Header({ services = [] }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll
  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);

  return (
    <header className={classNames(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl",
      scrolled
        ? "bg-white/95 shadow-[0_4px_18px_rgba(0,0,0,0.08)] border-b border-gray-200"
        : "bg-white/60 border-b border-transparent"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* TOP BAR */}
        <div className={classNames(
          "flex items-center justify-between transition-all duration-300",
          scrolled ? "h-16" : "h-20"
        )}>

          {/* LOGO */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={classNames(
              "rounded-2xl overflow-hidden bg-[#fee017]/20 border border-[#fee017]/40",
              "transition-all duration-300 group-hover:shadow-[0_4px_18px_rgba(254,224,23,0.45)] group-hover:scale-105",
              scrolled ? "p-2" : "p-2.5"
            )}>
              <img src={logo} alt="Logo"
                className={classNames(
                  "transition-all",
                  scrolled ? "w-10 h-10" : "w-12 h-12"
                )}
              />
            </div>

            <div className="hidden sm:flex flex-col">
              <span className={classNames(
                "font-black text-gray-900 tracking-tight",
                scrolled ? "text-lg" : "text-xl"
              )}>Tesis 1</span>
              <span className="text-xs font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#fee017] to-yellow-500">
                CONSULTORIA
              </span>
            </div>
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden lg:flex items-center gap-1">

            <NavLink to="/"
              className={({ isActive }) => classNames(
                "px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 font-medium transition-all",
                isActive
                  ? "bg-[#fee017]/25 text-gray-900 shadow-inner"
                  : "text-gray-700 hover:bg-[#fee017]/15"
              )}>
              <Icon.Home /> Inicio
            </NavLink>

            {/* SERVICES DROPDOWN */}
            <div className="relative group">
              <button className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 font-medium text-gray-700 hover:bg-[#fee017]/15 transition-all">
                <Icon.Services /> Servicios
                <Icon.Chevron open={false} />
              </button>

              <div className="absolute left-0 mt-2 w-[420px] bg-white border border-gray-200 rounded-2xl shadow-xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">

                <div className="grid grid-cols-2 gap-2">
                  {services.map((s, idx) => {
                    const path = s.path ? `/servicios/${s.path}` : `/servicios/${encodeURIComponent(s.name.toLowerCase().replaceAll(" ", "-"))}`;
                    return (
                    <Link key={s.name}
                      to={path}
                      className="px-4 py-3 text-sm font-medium rounded-xl text-gray-700 relative group/item overflow-hidden hover:bg-[#fee017]/20 transition-all"
                      style={{ animationDelay: `${idx * 30}ms` }}>

                      <div className="flex items-center justify-between">
                        {s.name}
                        <Icon.ExternalLink className="opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </div>

                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#fee017] rounded-r-full scale-y-0 group-hover/item:scale-y-100 transition-transform origin-top"></div>
                    </Link>
                  );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-center">
                  <Link
                    to="/servicios#entregas"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#fee017] text-white font-bold text-sm hover:opacity-95 transition"
                  >
                    Ver entregas exitosas
                  </Link>
                </div>
              </div>
            </div>

            <NavLink to="/nosotros"
              className={({ isActive }) => classNames(
                "px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 font-medium transition-all",
                isActive
                  ? "bg-[#fee017]/25 text-gray-900"
                  : "text-gray-700 hover:bg-[#fee017]/15"
              )}>
              <Icon.About /> Nosotros
            </NavLink>

            <NavLink to="/contacto"
              className={({ isActive }) => classNames(
                "px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 font-medium transition-all",
                isActive
                  ? "bg-[#fee017]/25 text-gray-900"
                  : "text-gray-700 hover:bg-[#fee017]/15"
              )}>
              <Icon.Contact /> Contacto
            </NavLink>

          </nav>

          {/* WHATSAPP + MOBILE BUTTON */}
          <div className="flex items-center gap-3">

            {/* Desktop WhatsApp */}
            <a
              href="https://wa.me/+51983318651?text=Hola, quiero información sobre los servicios"
              target="_blank" rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold text-sm rounded-xl
                bg-gradient-to-r from-[#19c464] to-[#139e50]
                hover:scale-[1.03] active:scale-[0.98]
                transition-all shadow-[0_4px_18px_rgba(25,196,100,0.35)]">
              <Icon.Whatsapp /> WhatsApp
            </a>

            {/* Mobile Menu */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2.5 rounded-xl hover:bg-gray-100 transition-all">
              {mobileMenuOpen ? <Icon.Close /> : <Icon.Menu />}
            </button>
          </div>

        </div>

        {/* MOBILE NAV */}
        <div
          className={classNames(
            "lg:hidden overflow-hidden transition-all duration-300",
            mobileMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <nav className="pb-6 pt-4">

            {/* Items */}
            <div className="space-y-1">

              <Link to="/" onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 flex items-center gap-3 rounded-xl hover:bg-[#fee017]/15">
                <Icon.Home /> Inicio
              </Link>

              {/* Accordion Servicios */}
              <div>
                <button
                  onClick={() => setServicesDropdown(!servicesDropdown)}
                  className="px-4 py-3 w-full flex items-center justify-between rounded-xl hover:bg-[#fee017]/15"
                >
                  <div className="flex items-center gap-3">
                    <Icon.Services /> Servicios
                  </div>
                  <Icon.Chevron open={servicesDropdown} />
                </button>

                <div className={classNames(
                  "overflow-hidden transition-all",
                  servicesDropdown ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                )}>
                  <div className="ml-4 pl-4 border-l-2 border-[#fee017]/40 mt-1 space-y-1">
                    {services.map(s => {
                      const path = s.path ? `/servicios/${s.path}` : `/servicios/${encodeURIComponent(s.name.toLowerCase().replaceAll(' ', '-'))}`;
                      return (
                      <Link key={s.name}
                        to={path}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setServicesDropdown(false);
                        }}
                        className="block px-4 py-2.5 text-sm rounded-lg text-gray-700 hover:bg-[#fee017]/20"
                      >
                        {s.name}
                      </Link>
                    );
                    })}
                    <div className="mt-2">
                      <Link
                        to="/servicios#entregas"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setServicesDropdown(false);
                        }}
                        className="block w-full text-center px-4 py-2.5 rounded-lg bg-[#fee017] text-white font-bold hover:opacity-95 transition"
                      >
                        Ver entregas exitosas
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <NavLink to="/nosotros" onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 flex items-center gap-3 rounded-xl hover:bg-[#fee017]/15">
                <Icon.About /> Nosotros
              </NavLink>

              <Link to="/contacto" onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 flex items-center gap-3 rounded-xl hover:bg-[#fee017]/15">
                <Icon.Contact /> Contacto
              </Link>

              {/* WhatsApp Mobile */}
              <a
                href="https://wa.me/+51983318651?text=Hola!"
                target="_blank" rel="noreferrer"
                className="w-full px-4 py-3.5 mt-4 flex items-center justify-center gap-2 text-white font-bold rounded-xl
                  bg-gradient-to-r from-[#19c464] to-[#139e50] shadow-lg">
                <Icon.Whatsapp /> Chatear por WhatsApp
              </a>

            </div>
          </nav>
        </div>

      </div>
    </header>
  );
}
